import React, { useState } from 'react';
import { Layers, Shapes, Scissors } from 'lucide-react';

const ExtraContextStep = ({ value, onChange }) => {
  const [showCutGrid, setShowCutGrid] = useState(false);

  const floorOptions = [
    { id: 'ground', label: 'Ground' },
    { id: 'middle', label: 'Middle' },
    { id: 'top', label: 'Top' },
    { id: 'duplex', label: 'Duplex' },
    { id: 'unsure', label: 'Not sure' }
  ];

  const shapeOptions = [
    { id: 'rectangular', label: 'Mostly rectangular' },
    { id: 'l-shaped', label: 'L-shaped' },
    { id: 'irregular', label: 'Very irregular' },
    { id: 'unsure', label: 'Not sure' }
  ];

  const cutOptions = [
    { id: 'yes', label: 'Yes' },
    { id: 'no', label: 'No' },
    { id: 'unsure', label: 'Not sure' }
  ];

  const gridDirections = [
    ['NW', 'N', 'NE'],
    ['W', 'Center', 'E'],
    ['SW', 'S', 'SE']
  ];

  const updateField = (field, val) => {
    const newValue = { ...value, [field]: val };
    if (field === 'cutsExtensions' && val !== 'yes') {
      newValue.cutLocations = [];
      setShowCutGrid(false);
    } else if (field === 'cutsExtensions' && val === 'yes') {
      setShowCutGrid(true);
    }
    onChange(newValue);
  };

  const toggleCutLocation = (location) => {
    const current = value.cutLocations || [];
    const newLocations = current.includes(location)
      ? current.filter(l => l !== location)
      : [...current, location];
    onChange({ ...value, cutLocations: newLocations });
  };

  return (
    <div>
      <h3 className="text-2xl font-bold text-[#232323] mb-2">A few more details about your space</h3>
      <p className="text-[#353535] mb-6">Help us understand your property better with these final questions.</p>
      
      {/* Floor level */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Layers className="w-5 h-5 text-[#232323]" />
          <h4 className="font-bold text-[#232323]">Floor level</h4>
        </div>
        <div className="flex flex-wrap gap-2">
          {floorOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => updateField('floor', option.id)}
              className={`px-4 py-2 rounded-lg border-2 font-semibold text-sm transition-all ${
                value.floor === option.id
                  ? 'bg-[#232323] text-white border-[#232323]'
                  : 'bg-white text-[#232323] border-gray-300 hover:border-[#232323]'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Shape */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Shapes className="w-5 h-5 text-[#232323]" />
          <h4 className="font-bold text-[#232323]">Shape</h4>
        </div>
        <div className="flex flex-wrap gap-2">
          {shapeOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => updateField('shape', option.id)}
              className={`px-4 py-2 rounded-lg border-2 font-semibold text-sm transition-all ${
                value.shape === option.id
                  ? 'bg-[#232323] text-white border-[#232323]'
                  : 'bg-white text-[#232323] border-gray-300 hover:border-[#232323]'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Cuts/extensions */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Scissors className="w-5 h-5 text-[#232323]" />
          <h4 className="font-bold text-[#232323]">Cuts or extensions</h4>
        </div>
        <p className="text-sm text-[#353535] mb-3">Does your property have missing corners or extended portions?</p>
        <div className="flex flex-wrap gap-2">
          {cutOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => updateField('cutsExtensions', option.id)}
              className={`px-4 py-2 rounded-lg border-2 font-semibold text-sm transition-all ${
                value.cutsExtensions === option.id
                  ? 'bg-[#232323] text-white border-[#232323]'
                  : 'bg-white text-[#232323] border-gray-300 hover:border-[#232323]'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Cut locations grid (conditional) */}
      {showCutGrid && value.cutsExtensions === 'yes' && (
        <div className="bg-[var(--accent-blue-200)] rounded-lg p-4">
          <h4 className="font-bold text-[#232323] mb-3">Where are the cuts/extensions?</h4>
          <p className="text-sm text-[#353535] mb-3">Tap all areas that apply (optional).</p>
          <div className="grid grid-cols-3 gap-2">
            {gridDirections.map((row, rowIdx) => (
              row.map((dir, colIdx) => {
                const cellId = dir.toLowerCase().replace(' ', '-');
                const isSelected = (value.cutLocations || []).includes(cellId);
                return (
                  <button
                    key={`${rowIdx}-${colIdx}`}
                    onClick={() => toggleCutLocation(cellId)}
                    disabled={dir === 'Center'}
                    className={`p-3 rounded-lg border-2 text-xs font-bold transition-all ${
                      isSelected
                        ? 'bg-[#232323] text-white border-[#232323]'
                        : 'bg-white text-[#232323] border-gray-300 hover:border-[#232323]'
                    } ${
                      dir === 'Center' ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {dir}
                  </button>
                );
              })
            ))}
          </div>
          <button
            onClick={() => onChange({ ...value, cutLocations: ['unsure'] })}
            className="w-full mt-3 p-2 border-2 border-gray-300 bg-white rounded-lg text-sm font-semibold hover:border-[#232323]"
          >
            Not sure / No idea
          </button>
        </div>
      )}

      <div className="mt-6 bg-green-50 border-2 border-green-200 rounded-lg p-4">
        <p className="text-sm text-green-800">
          🎉 <strong>Almost done!</strong> Click "Get My Report" to see your personalized Vastu analysis.
        </p>
      </div>
    </div>
  );
};

export default ExtraContextStep;